/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import { SidebarData, SidebarMenuType } from "../../assets/js/SidebarConstant";
import { cn } from "@nextui-org/theme";
import useTicketStore from "../../stores/useTicketStore";
import { useQueryParams, ParamKey } from "../../hooks/useQueryParams";
import { useLocation, useNavigate } from "react-router-dom";
import Route from "../../assets/js/Route";
import AppUserProfile from "../../assets/js/AppUserProfile";
import { EntryPoints } from "../../constants/entryPoint";
import { ReactComponent as RevertBack } from "../../assets/icons/revert-back.svg";
import { annoucements } from "../../constants/annoucement";

const Sidebar = ({ isOpen }) => {
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  const {
    setSelectedPhase,
    setSelectedMenu,
    resetFiltersKeepMenu,
    selectedMenu,
  } = useTicketStore();

  const useQueryParamsHook = useQueryParams();
  const navigate = useNavigate();
  const location = useLocation();

  const userProfile = AppUserProfile.getData();
  const userRole = userProfile?.whoami;

  const redirectToParent = () => {
    const entryPoint = annoucements.terminateStandAlone[userRole].link;
    console.log(entryPoint);
    window.location.href = entryPoint;
  };

  const findMenuItemById = (items, targetId) => {
    for (const item of items) {
      if (item.id === targetId) {
        return item;
      }
      if (item.subMenu) {
        const found = findMenuItemById(item.subMenu, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const findMatchingMenuItem = (items, targetPhase) => {
    for (const item of items) {
      if (item.phaseData && !item.phaseData.isSelectAll) {
        if (targetPhase && item.phaseData) {
          if (item.phaseData.phases && targetPhase.phases) {
            const itemPhases = [...item.phaseData.phases].sort();
            const targetPhases = [...targetPhase.phases].sort();
            if (JSON.stringify(itemPhases) === JSON.stringify(targetPhases)) {
              return item;
            }
          }

          if (item.phaseData.id && targetPhase.id) {
            if (
              typeof item.phaseData.id === "object" &&
              typeof targetPhase.id === "object"
            ) {
              if (item.phaseData.id.value === targetPhase.id.value) {
                return item;
              }
            } else if (item.phaseData.id === targetPhase.id) {
              return item;
            }
          }

          if (
            item.phaseData.value &&
            targetPhase.value &&
            item.phaseData.value === targetPhase.value
          ) {
            return item;
          }
        }
      }

      if (item.subMenu) {
        const found = findMatchingMenuItem(item.subMenu, targetPhase);
        if (found) return found;
      }
    }
    return null;
  };

  useEffect(() => {
    if (hasInitialized) return;

    const initializeFromUrl = () => {
      const urlMenu = useQueryParamsHook.getParam(ParamKey.Menu);
      const urlPhases = useQueryParamsHook.getParam(ParamKey.Phases);

      let menuItemToSelect = null;

      if (selectedMenu) {
        menuItemToSelect = findMenuItemById(SidebarData, selectedMenu);
        if (menuItemToSelect) {
          setSelectedMenuItem(menuItemToSelect.id);

          useQueryParamsHook.setParam("menu", menuItemToSelect.id);
          setHasInitialized(true);
          return;
        }
      }

      if (location.pathname === Route.PowerBi) {
        const powerBiItem = SidebarData.find(
          (item) => item.id === SidebarMenuType.PowerBI
        );

        if (powerBiItem) {
          setSelectedMenuItem(powerBiItem.id);
          setSelectedMenu(powerBiItem.id, useQueryParamsHook);
          useQueryParamsHook.setParam("menu", powerBiItem.id);
          setHasInitialized(true);
          return;
        }
      }

      if (urlMenu) {
        menuItemToSelect = findMenuItemById(SidebarData, urlMenu);
        if (menuItemToSelect) {
          setSelectedMenuItem(menuItemToSelect.id);
          setSelectedMenu(menuItemToSelect.id, useQueryParamsHook);

          if (menuItemToSelect.phaseData) {
            if (menuItemToSelect.phaseData.isSelectAll) {
              setSelectedPhase(null, useQueryParamsHook);
            } else {
              setSelectedPhase(menuItemToSelect.phaseData, useQueryParamsHook);
            }
          }
        }
      }

      if (!menuItemToSelect && urlPhases) {
        const phaseData = JSON.parse(urlPhases);
        if (phaseData) {
          menuItemToSelect = findMatchingMenuItem(SidebarData, phaseData);
          if (menuItemToSelect) {
            setSelectedMenuItem(menuItemToSelect.id);
            setSelectedMenu(menuItemToSelect.id, useQueryParamsHook);
            useQueryParamsHook.setParam("menu", menuItemToSelect.id);
          }
        }
      }

      if (!menuItemToSelect) {
        const allTicketsItem = SidebarData.find(
          (item) => item.phaseData?.isSelectAll
        );

        if (allTicketsItem) {
          setSelectedMenuItem(allTicketsItem.id);
          setSelectedMenu(allTicketsItem.id, useQueryParamsHook);
          setSelectedPhase(null, useQueryParamsHook);
          useQueryParamsHook.setParam("menu", allTicketsItem.id);
        }
      }

      setHasInitialized(true);
    };

    initializeFromUrl();
  }, []);

  useEffect(() => {
    if (!hasInitialized) return;

    if (location.pathname === Route.PowerBi) {
      const powerBiItem = SidebarData.find(
        (item) => item.id === SidebarMenuType.PowerBI
      );

      if (powerBiItem && selectedMenuItem !== powerBiItem.id) {
        setSelectedMenuItem(powerBiItem.id);
        setSelectedMenu(powerBiItem.id, useQueryParamsHook);
        useQueryParamsHook.setParam("menu", powerBiItem.id);
      }
    }
  }, [location.pathname, hasInitialized, selectedMenuItem]);

  useEffect(() => {
    if (hasInitialized && selectedMenu && selectedMenuItem !== selectedMenu) {
      setSelectedMenuItem(selectedMenu);
    }
  }, [selectedMenu, hasInitialized]);

  const handleMenuSelection = (menuItem) => {
    resetFiltersKeepMenu();
    setSelectedMenuItem(menuItem.id);
    setSelectedMenu(menuItem.id, useQueryParamsHook);

    setSelectedMenu(menuItem.id, useQueryParamsHook);

    if (menuItem.phaseData) {
      if (menuItem.phaseData.isSelectAll) {
        setSelectedPhase(null, useQueryParamsHook);
      } else {
        setSelectedPhase(menuItem.phaseData, useQueryParamsHook);
      }
    } else {
      setSelectedPhase(null, useQueryParamsHook);
    }

    if (menuItem.id === SidebarMenuType.PowerBI) {
      useQueryParamsHook.setParams({ menu: menuItem.id });
    }

    navigate(menuItem.path);
  };

  const isMenuItemActive = (menuItem) => {
    return selectedMenuItem === menuItem.id;
  };

  const hasPermission = (item) => {
    if (!item.allowRoles || item.allowRoles.length === 0) {
      return true;
    }

    const userData = AppUserProfile.getData();
    const userRole = userData.role_name || userData.whoami;

    return item.allowRoles.includes(userRole);
  };

  const renderMenuItem = (item, depth = 0, parentId = "") => {
    const isActive = isMenuItemActive(item);
    const opacityClass = depth > 0 ? "opacity-90" : "";

    const uniqueKey = parentId ? `${parentId}-${item.id}` : `${item.id}`;

    if (!hasPermission(item)) {
      return null;
    }

    return (
      <React.Fragment key={uniqueKey}>
        <div className="relative">
          <div
            className={cn([
              "flex w-full",
              depth === 0 ? "justify-center" : "justify-start",
              depth > 0 && "mb-2",
            ])}
          >
            <div
              className={cn([
                "flex items-center gap-x-5",
                "rounded-lg text-white text-nowrap py-2 cursor-pointer",
                "w-full",
                "text-base",
                opacityClass,
                !isActive && "hover:bg-slate-800",
                !isOpen ? "justify-center" : "px-2",
                isActive && "bg-white text-black",
                "transition-all delay-0 duration-300 ease-in-out",
              ])}
              onClick={() => handleMenuSelection(item)}
              style={depth > 0 ? { marginLeft: depth * 55 } : {}}
            >
              {item.icon && (
                <span
                  className={cn(["flex-shrink-0", isActive && "text-black"])}
                >
                  {item.icon}
                </span>
              )}
              {isOpen && (
                <div
                  className={cn([
                    "transition-all duration-300 ease-in-out",
                    isOpen ? "w-fit opacity-100" : "max-w-0 opacity-0",
                  ])}
                >
                  <p className="text-wrap text-base">{item.title}</p>
                </div>
              )}
            </div>
          </div>

          <div
            className={cn([
              "transition-all duration-300 ease-in-out pt-1",
              isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0",
            ])}
          >
            {item.subMenu && item.subMenu.length > 0 && (
              <div className="relative">
                <div
                  className="absolute w-0.5 bg-white top-2 bottom-0"
                  style={{ left: depth + 20 }}
                ></div>
                {item.subMenu.map((subItem) =>
                  renderMenuItem(subItem, depth + 1, uniqueKey)
                )}
              </div>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  };

  return (
    <div
      className={cn([
        "bg-black flex flex-col py-1 relative overflow-hidden h-full",
        "transition-all duration-300 ease-in-out",
      ])}
      style={{
        width: isOpen ? "280px" : "100px",
        paddingLeft: isOpen ? "18px" : "13px",
        paddingRight: isOpen ? "18px" : "13px",
      }}
    >
      <div className="flex-1 overflow-y-auto py-2">
        {SidebarData.map((item, index) =>
          renderMenuItem(item, 0, `root-${index}`)
        )}
      </div>
      <div className="mt-auto pb-12">
        {userRole && (
          <div
            className="flex hover:bg-slate-800 items-center gap-x-4 rounded-lg px-4 py-2 cursor-pointer"
            onClick={redirectToParent}
          >
            <RevertBack className="text-white" />
            <p className="text-white">กลับไป {EntryPoints[userRole]?.parent}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
